import React, { Component } from "react"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { fetchKoalaData } from "apis/koala"
import fetchAPI from "AtomicUtils/fetchAPI"
import isEqual from "lodash/isEqual"
import PropTypes from "prop-types"
import { withRadium } from "higher-order-components"
import { ExpandableArea } from "components"
import { AnimatedPlusMinus } from "containers"
import COLOR_PALETTE from "constants/color-palette"
import { Route, Switch, withRouter } from "react-router-dom"
import cookieHandler from "AtomicUtils/cookieHandler"
import gtmHandler from "AtomicUtils/gtmHandler"
import { actionCreators, selectors } from "store/actions/mymazda"
import PageWithData from "./page"
import NavigationLayout from "./navigation/navigation-layout"
import Breadcrumb from "./navigation/breadcrumb"
import TopNavigation from "./navigation/top-navigation"
import { PrimaryNavigationItem, SecondaryNavigationItem } from "./navigation/navigation-item"
import NotificationsDropdown, { NotificationsButton } from "./navigation/notifications"
import AccountDropdown, { AccountButton } from "./navigation/account"

class MyMazdaContainer extends Component {
  constructor() {
    super()
    this.isActiveRoute = this.isActiveRoute.bind(this)
    this.hasActiveRoutes = this.hasActiveRoutes.bind(this)

    this.state = {
      loading: false
    }
  }
  componentWillMount() {
    const {
      registerRoutes,
      initialComponents,
      initialComponentsRoute,
      hydrateRouteComponents,
      routerDefinition,
      toggleNavItem,
      mainNavItems
    } = this.props

    const routes = routerDefinition.reduce(
      (allRoutes, { route, ...properties }) => ({
        ...allRoutes,
        [route]: properties
      }),
      {}
    )

    registerRoutes(routes)
    hydrateRouteComponents(initialComponentsRoute, initialComponents)

    // update ui to expand the correct navigation item
    const initialActiveItem = mainNavItems.findIndex(this.hasActiveRoutes) || 0
    toggleNavItem(initialActiveItem)
  }

  componentDidUpdate(prevProps) {
    const {
      location,
      closeAllNav,
      updateRouteComponents,
      notifications,
      saveNotifications,
      allNotifications,
      ui: { notificationMenuOpen }
    } = this.props
    const unreadNotifications = allNotifications && allNotifications.length ? allNotifications.filter(item => item.notificationStatus === "NEW") : []

    if (location.key !== prevProps.location.key) {
      closeAllNav()
      updateRouteComponents(location.pathname, location.search)
    }

    if (!isEqual(notifications.notificationItems, prevProps.notifications.notificationItems)) {
      saveNotifications(notifications.notificationItems)
    }

    if (prevProps.ui.notificationMenuOpen && !notificationMenuOpen && unreadNotifications.length) {
      fetchKoalaData(
        notifications.notificationsEndpoint,
        unreadNotifications.map(({ notificationID }) => ({ notificationID, notificationStatus: "VIEWED" }))
      ).then(data => {
        if (!data) return
        saveNotifications(data)
      }).catch(error => {
        throw error
      })
    }
  }

  fetchNotifications = async () => {
    await setTimeout(async () => {
      const { notifications, saveNotifications } = this.props
      const { loading } = this.state
      if (!loading) {
        await this.setState({
          loading: true
        }, async () => {
          await fetchAPI({
            method: "GET",
            url: notifications.notificationsEndpoint
          })
          .then(async (response) => {
            let json = {}
            if (response.status === 200) json = await response.json()
            const { data, success } = json
            if (!success) {
              this.setState({
                loading: false
              })
            } else {
              this.setState({
                loading: false
              }, () => {
                saveNotifications(data)
              })
            }
          })
          .catch(() => {
            this.setState({
              loading: false
            })
          })
        })
      }
      this.fetchNotifications()
    }, 10 * 60 * 1000)
  }

  componentDidMount() {
    const { notifications: { notificationItems }, saveNotifications, account } = this.props
    if (notificationItems) saveNotifications(notificationItems)
    this.fetchNotifications()

    if (account && account.userId)  {
      new cookieHandler().setCookie("userId",account.userId)
    }
  }

  isActiveRoute(href) {
    /*
      this should be true for any route under the navigation href
      eg for /mymazda/1/servicing the following pathnames will be true.
      - /mymazda/1/servicing/summary
      - /mymazda/1/servicing/request
      - /mymazda/1/servicing/deeply/nested/route
    */
    return this.props.location.pathname.indexOf(href) > -1
  }

  hasActiveRoutes({ href, items = [] }) {
    return (
      this.isActiveRoute(href) ||
      items.some(item => this.isActiveRoute(item.href))
    )
  }

  render() {
    const {
      routes,
      mainNavItems,
      notifications,
      allNotifications,
      account,
      breadcrumb,
      toggleNav,
      toggleUser,
      toggleNotifications,
      expandNavItem,
      closeAllNav,
      toggleNavItem,
      ui: {
        navigationOpen,
        expandedNavItemIndex,
        notificationMenuOpen,
        userMenuOpen
      }
    } = this.props

    const manyNestedItems =
      mainNavItems
        .filter(({ items }) => typeof items !== "undefined")
        .length > 1 // more than 1 mainNavItems with nested items

    const unreadNotifications = allNotifications && allNotifications.length ? allNotifications.filter(item => item && item.notificationStatus === "NEW") : []

    return (
      <div style={styles.container}>
        <NavigationLayout
          topNav={
            <TopNavigation>
              <div><Breadcrumb hideOnMobile items={breadcrumb} /></div>
              <div style={{ display: "flex", alignItems: "center" }}>
                {notifications &&
                  <NotificationsButton
                    open={notificationMenuOpen}
                    onClick={() => {
                      gtmHandler({
                        pageCategory: "MyMazda - Notifications",
                        pageAction: `Drop down - "${notificationMenuOpen ? 'Collapsed' : 'Expanded'}"`
                      })
                      toggleNotifications()
                    }}
                    notifications={unreadNotifications} />
                }
                {account &&
                  <AccountButton
                    open={userMenuOpen}
                    name={account.name}
                    onClick={toggleUser} />
                }
              </div>
            </TopNavigation>
          }
          darkenBg={navigationOpen || userMenuOpen || notificationMenuOpen}
          topNavPanels={[
            <NotificationsDropdown
              open={notificationMenuOpen}
              {...notifications}
              notifications={allNotifications} />,
            <AccountDropdown
              key={"account-dropdown"}
              open={userMenuOpen}
              items={account.items} />
          ]}
          onBackgroundClick={closeAllNav}
          onNavButtonClick={toggleNav}
          sideNavOpen={navigationOpen}
          sideNav={mainNavItems.map(({
            items = [],
            label,
            href,
            description,
            imageUrl
          }, index) => (
            <div key={href.split("/").join("-")}>
              <ExpandableArea
                isExpanded={!manyNestedItems || expandedNavItemIndex === index}
                onToggle={() => toggleNavItem(index)}>
                {({
                  RenderArea,
                  isExpanded,
                  toggleExpanded
                }) => (
                  <div style={{ borderBottom: `1px solid ${COLOR_PALETTE.GRAY2}` }}>
                    <PrimaryNavigationItem
                      active={this.isActiveRoute(href)}
                      onClick={() => expandNavItem(index)}
                      {...{
                        label, href, description, imageUrl
                      }}>
                      {manyNestedItems &&
                      !!items.length &&
                        <span
                          style={styles.expandButton}
                          onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleExpanded()
                          }
                          }>
                          <AnimatedPlusMinus styleOverride={{ background: "#979797" }} open={isExpanded} />
                        </span>
                      }
                    </PrimaryNavigationItem>
                    <RenderArea>
                      {items &&
                        <div style={{
                          background: COLOR_PALETTE.GRAY3
                        }}>
                          {items.map(subItemProps => (
                            <SecondaryNavigationItem
                              onClick={() => expandNavItem(index)}
                              active={this.isActiveRoute(subItemProps.href)}
                              key={subItemProps.href.split("/").join("-")}
                              label={subItemProps.label}
                              href={subItemProps.href} />
                          ))}
                        </div>
                      }
                    </RenderArea>
                  </div>)}
              </ExpandableArea>
            </div>
          ))}>
          <Switch>
            {Object.entries(routes).map(([route]) => (
              <Route
                key={route}
                path={route}
                component={PageWithData} />
            ))}
          </Switch>
        </NavigationLayout>
      </div>
    )
  }
}

const routeValidation = (props, propName, componentName) => {
  if (!/^(\/.*)/.test(props[propName])) {
    return new Error(`invalid prop "${propName}" supplied to "${componentName}". (should begin with "/")`)
  }
}

MyMazdaContainer.propTypes = {
  registerRoutes: PropTypes.func,
  initialComponents: PropTypes.array,
  initialComponentsRoute: PropTypes.string,
  hydrateRouteComponents: PropTypes.func,
  updateRouteComponents: PropTypes.func,
  closeAllNav: PropTypes.func,
  routerDefinition: PropTypes.arrayOf(
    PropTypes.shape({
      route: routeValidation,
      breadcrumb: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          href: PropTypes.string
        })
      ).isRequired,
      fetchUrl: PropTypes.string,
      name: PropTypes.string
    })
  ),
  location: PropTypes.object,
  routes: PropTypes.object,
  mainNavItems: PropTypes.array,
  notifications: PropTypes.object,
  allNotifications: PropTypes.array,
  saveNotifications: PropTypes.func,
  account: PropTypes.object,
  toggleNav: PropTypes.func,
  toggleUser: PropTypes.func,
  toggleNotifications: PropTypes.func,
  expandNavItem: PropTypes.func,
  toggleNavItem: PropTypes.func,
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      href: PropTypes.string
    })
  ),
  ui: PropTypes.shape({
    navigationOpen: PropTypes.bool,
    expandedNavItemIndex: PropTypes.number,
    notificationMenuOpen: PropTypes.bool
  })
}

const styles = {
  container: {
    position: "relative"
  },
  expandButton: {
    display: "flex",
    height: "100%",
    paddingLeft: "1.5em",
    borderLeft: `1px solid ${COLOR_PALETTE.GRAY2}`,
    marginLeft: "1em"
  },
  body: (compact) => ({
    textAlign: "left",
    position: "relative",
    marginLeft: compact ? "0px" : "320px",
    width: compact ? "100%" : "calc(100% - 320px)"
  })
}

const mapStateToProps = (state, { location }) => ({
  routes: selectors.getRoutes(state),
  ui: selectors.getUiState(state),
  breadcrumb: selectors.getBreadcrumb(location.pathname)(state),
  allNotifications: selectors.getNotifications(state)
})

const mapDispatchToProps = dispatch => bindActionCreators(actionCreators, dispatch)

/** Top Level MyMazda Container. */
export default withRadium(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
)(MyMazdaContainer)



// WEBPACK FOOTER //
// ./src/containers/mymazda/index.jsx